<template>
    <!-- MYAC-883 — the post-run review window. The extraction has finished but written
         NOTHING: this shows exactly what would be added, changed and removed, and the
         register only moves once the user approves. A destructive-looking delta (a
         register shrinking or exploding, or emptied outright) is flagged above the fold. -->
    <div class="flex flex-col items-start">
        <Card class="w-full max-w-2xl border border-primary-200 bg-primary-50/30"
            :pt="{ body: { class: 'p-4' } }">
            <template #content>
                <div class="flex items-start gap-2 mb-2">
                    <i class="pi pi-list-check text-primary-600 mt-0.5" />
                    <div class="flex-1 min-w-0">
                        <div class="flex items-center gap-2 flex-wrap">
                            <span class="font-semibold text-sm text-surface-700">
                                {{ ct('chat.register_delta.title', { register: registerLabel }) }}
                            </span>
                            <AiGeneratedTag />
                        </div>
                        <div class="text-xs text-surface-500 mt-0.5">
                            {{ ct(`chat.register_delta.mode.${proposal.mode}`) }}
                        </div>
                    </div>
                </div>

                <Message v-for="flag in guardrailFlags" :key="flag" severity="warn" size="small" class="mb-2">
                    {{ ct(`chat.register_delta.guardrail.${flag}`) }}
                </Message>

                <div class="flex flex-wrap gap-2 mb-3">
                    <Tag severity="success" :value="ct('chat.register_delta.added', { n: delta.added_count })" />
                    <Tag severity="info" :value="ct('chat.register_delta.updated', { n: delta.updated_count })" />
                    <Tag severity="danger" :value="ct('chat.register_delta.removed', { n: delta.removed_count })" />
                    <Tag severity="secondary"
                        :value="ct('chat.register_delta.unchanged', { n: delta.unchanged_count }, delta.unchanged_count)" />
                </div>

                <Message v-if="delta.dropped_columns.length" severity="warn" size="small" variant="simple"
                    class="mb-3">
                    {{ ct('chat.register_delta.dropped_columns', { columns: delta.dropped_columns.join(', ') }) }}
                </Message>

                <!-- Same table rendering as the MYAC-848 append/delete previews: real
                     columns, sticky header, horizontal scroll. Updated cells that
                     actually change are highlighted. -->
                <Tabs v-if="hasSamples" v-model:value="activeTab" :pt="{ root: { class: 'bg-transparent' } }">
                    <TabList :pt="{ tabList: { class: 'bg-transparent' } }">
                        <Tab v-for="bucket in buckets" :key="bucket.key" :value="bucket.key"
                            class="text-sm py-2 px-3">
                            {{ ct(`chat.register_delta.tab.${bucket.key}`) }} ({{ bucket.count }})
                        </Tab>
                    </TabList>
                    <TabPanels :pt="{ root: { class: 'bg-transparent px-0 pt-2 pb-0' } }">
                        <TabPanel v-for="bucket in buckets" :key="bucket.key" :value="bucket.key">
                            <div class="border border-surface-200 rounded overflow-auto max-h-64">
                                <table class="w-full text-xs">
                                    <thead class="sticky top-0 bg-surface-100">
                                        <tr>
                                            <th v-for="col in columnsFor(bucket)" :key="col.key"
                                                class="text-left px-2 py-1 font-medium whitespace-nowrap">
                                                {{ col.label }}
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr v-for="(row, i) in bucket.rows" :key="`${bucket.key}-${i}`"
                                            class="border-t border-surface-100">
                                            <td v-for="col in columnsFor(bucket)" :key="col.key"
                                                class="px-2 py-1 align-top"
                                                :class="bucket.key === 'updated' && row.changed?.includes(col.key)
                                                    ? 'bg-amber-50 font-medium' : ''">
                                                {{ row.cells?.[col.key] ?? '-' }}
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                            <p v-if="bucket.count > bucket.rows.length" class="text-xs text-surface-500 mt-2">
                                {{ ct('chat.register_delta.and_more', { n: bucket.count - bucket.rows.length }) }}
                            </p>
                        </TabPanel>
                    </TabPanels>
                </Tabs>

                <div class="flex justify-end gap-2 mt-3">
                    <Button size="small" severity="secondary" outlined :disabled="busy"
                        :label="ct('chat.register_delta.decline')" @click="respond('decline')" />
                    <Button size="small" icon="pi pi-check" :disabled="busy"
                        :label="ct('chat.register_delta.approve')" @click="respond('approve')" />
                </div>
            </template>
        </Card>
    </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import Button from 'primevue/button'
import Card from 'primevue/card'
import Message from 'primevue/message'
import Tag from 'primevue/tag'
import Tabs from 'primevue/tabs'
import TabList from 'primevue/tablist'
import Tab from 'primevue/tab'
import TabPanels from 'primevue/tabpanels'
import TabPanel from 'primevue/tabpanel'
import AiGeneratedTag from '@/components/shared/AiGeneratedTag.vue'
import { useChatbotStore } from '@/stores/chatbot'
import { useCardLocale } from '@/composables/useCardLocale'
import type { RegisterDeltaRow, RegisterProposal } from '@/stores/registerProposals'

const props = defineProps<{ proposal: RegisterProposal }>()

const chatbotStore = useChatbotStore()
const { ct } = useCardLocale(computed(() => chatbotStore.conversationLanguage ?? undefined))

const busy = ref(false)
const activeTab = ref<string>('added')

const delta = computed(() => props.proposal.delta)
const registerLabel = computed(() => ct(`chat.action_approval.register.${props.proposal.register_key}`))

// Only flags this front knows how to phrase — a flag added by a newer backend is
// dropped rather than rendered as a missing translation key.
const KNOWN_FLAGS = ['growth_over_threshold', 'shrink_over_threshold', 'register_emptied']
const guardrailFlags = computed(() =>
    (delta.value.guardrail_flags ?? []).filter(f => KNOWN_FLAGS.includes(f)))

const buckets = computed(() =>
    [
        { key: 'added', count: delta.value.added_count, rows: delta.value.added ?? [] },
        { key: 'updated', count: delta.value.updated_count, rows: delta.value.updated ?? [] },
        { key: 'removed', count: delta.value.removed_count, rows: delta.value.removed ?? [] },
    ].filter(b => b.count > 0))

const hasSamples = computed(() => buckets.value.length > 0)

/**
 * The column headers for one bucket's table — the proposed table's contract,
 * narrowed to columns its sample rows actually fill (a wide register would
 * otherwise be mostly dashes). REMOVED rows on a destructive run may carry the
 * OLD schema's keys; when nothing matches the new contract, derive headers from
 * the rows themselves so the data still shows.
 */
function columnsFor(bucket: { rows: RegisterDeltaRow[] }): { key: string; label: string }[] {
    const filled = new Set<string>()
    for (const row of bucket.rows) {
        for (const [key, value] of Object.entries(row.cells ?? {})) {
            if (typeof value === 'string' && value.trim() !== '') filled.add(key)
        }
    }
    const declared = (props.proposal.proposed_table?.columns ?? []).filter(c => filled.has(c.key))
    if (declared.length) return declared
    return [...filled].map(key => ({ key, label: key }))
}

async function respond(decision: 'approve' | 'decline') {
    if (busy.value) return
    busy.value = true
    try {
        await chatbotStore.respondToRegisterProposal(
            props.proposal.id, props.proposal.register_key, decision)
    } finally {
        busy.value = false
    }
}
</script>
